import React, { useState, useMemo } from "react";
import { MarkdownPreview } from "../../../MarkdownPreview";
import { Chip } from "../../../Chip";
import { NumberedStep } from "./NumberedStep";
import { BodyText } from "./BodyText";
import { LanguageSelector } from "./LanguageSelector";
import { LocalInferenceInstallationStep } from "./LocalInferenceInstallationStep";
import {
    WorkflowParameter,
    LanguageOption,
    API_KEY_PLACEHOLDER,
    generateVideoApiPythonCode,
    generateVideoApiJavaScriptCode,
    getProductionConnectorSnippet,
    getProductionBackendSnippet,
    replaceApiKeyPlaceholder,
    VideoSourceType,
    VideoStreamingOutputs,
    PYTHON_INSTALL_CODE,
    JS_INSTALL_CODE
} from "../DeployCodeGenerators";
import { trackEvent } from "../../workflowUtils";
import { WorkflowEvents } from "../../analytics/WorkflowEvents";
import { getCompatibleWorkflowStepOutputRefs } from "../../workflowManifest";
import { useProjectSelector } from "../../../../hooks/useProjectSelector";

export const VIDEO_API_LANGUAGES: LanguageOption[] = [
    { text: "JavaScript (React/Express)", value: "javascript" },
    { text: "Python", value: "python" }
];

const VIDEO_SOURCE_OPTIONS: { text: string; value: VideoSourceType; icon: string }[] = [
    { text: "Webcam", value: "webcam", icon: "far fa-webcam" },
    { text: "RTSP Stream", value: "rtsp", icon: "far fa-signal-stream" },
    { text: "Video File", value: "video_file", icon: "far fa-file-video" }
];

export type VideoDeploymentContentProps = {
    workflowUrl: string;
    workspaceUrl: string;
    apiKey: string | null;
    workflowParameters: WorkflowParameter[];
    isLocal: boolean;
    workflowConfigJSON: string | null;
    isWorkflowEditor?: boolean;
};

function getAvailableVideoStreams(workflowSpec: any): {
    videoOutputs: string[];
    dataOutputNames: string[];
} {
    const imageOutputRefs = getCompatibleWorkflowStepOutputRefs(
        workflowSpec,
        ["image"],
        undefined,
        false
    );

    const videoOutputs =
        workflowSpec.outputs
            ?.filter((output: any) => imageOutputRefs.includes(output.selector))
            .map((output: any) => output.name) || [];

    const dataOutputNames =
        workflowSpec.outputs
            ?.filter((output: any) => !imageOutputRefs.includes(output.selector))
            .map((output: any) => output.name) || [];

    return { videoOutputs, dataOutputNames };
}

export function VideoDeploymentContent({
    workflowUrl,
    workspaceUrl,
    apiKey,
    workflowParameters,
    isLocal,
    workflowConfigJSON,
    isWorkflowEditor = true
}: VideoDeploymentContentProps) {
    const project = useProjectSelector();
    // For local, we use localhost; for hosted, we use the project's serverless URL or default
    const serverUrl = isLocal
        ? "http://localhost:9001"
        : (project?.serverlessUrl?.() ?? "https://serverless.roboflow.com");

    const streamingOutputs = useMemo((): VideoStreamingOutputs | undefined => {
        if (!workflowConfigJSON) return undefined;

        try {
            const parsedConfig = JSON.parse(workflowConfigJSON);
            const workflowSpec = parsedConfig?.specification || {};
            const { videoOutputs, dataOutputNames } = getAvailableVideoStreams(workflowSpec);

            if (videoOutputs.length === 0 && dataOutputNames.length === 0) {
                return undefined;
            }

            return {
                streamOutputName: videoOutputs[0] || null,
                dataOutputNames: dataOutputNames.length > 0 ? dataOutputNames : ["predictions"]
            };
        } catch {
            return undefined;
        }
    }, [workflowConfigJSON]);

    const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>(
        VIDEO_API_LANGUAGES[1] // Default to Python
    );
    const [selectedSource, setSelectedSource] = useState<VideoSourceType>("webcam");

    const getCodeSnippet = () => {
        if (selectedLanguage.value === "python") {
            return generateVideoApiPythonCode(
                workflowUrl,
                workspaceUrl,
                API_KEY_PLACEHOLDER,
                workflowParameters,
                selectedSource,
                serverUrl,
                streamingOutputs,
                isLocal
            );
        }
        return generateVideoApiJavaScriptCode(
            workflowUrl,
            workspaceUrl,
            API_KEY_PLACEHOLDER,
            workflowParameters,
            selectedSource,
            serverUrl,
            streamingOutputs,
            isLocal
        );
    };

    let stepCounter = 1;
    const getStepNumber = () => stepCounter++;

    return (
        <div className="space-y-4 p-2">
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    {!isLocal && isWorkflowEditor && (
                        <>
                            <Chip color="green" size="xs">
                                Recommended
                            </Chip>
                            <Chip color="blue" size="xs">
                                Low Code
                            </Chip>
                        </>
                    )}
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isLocal ? "Local Video Processing" : "Video Streaming API"}
                    </h3>
                </div>
            </div>

            <BodyText>
                {isLocal
                    ? "Process video files or streams with your workflow locally using the Inference Server."
                    : "Stream video from webcams, RTSP cameras, or video files via WebRTC and receive inference results in real-time."}
                {!isLocal && (
                    <>
                        {" "}
                        <a
                            href="https://docs.roboflow.com/deploy/serverless-video-streaming-api"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand hover:underline"
                        >
                            Learn more →
                        </a>
                    </>
                )}
            </BodyText>

            {isLocal && <LocalInferenceInstallationStep number={getStepNumber()} />}

            <NumberedStep number={getStepNumber()} title="Select Your Language" collapsible={true}>
                <div className="flex-col !gap-2">
                    <LanguageSelector
                        selectedLanguage={selectedLanguage}
                        onLanguageSelect={setSelectedLanguage}
                        languages={VIDEO_API_LANGUAGES}
                    />
                </div>
            </NumberedStep>

            <NumberedStep number={getStepNumber()} title="Install the SDK" collapsible={true}>
                <div className="flex-col !gap-2">
                    <BodyText>
                        Install the{" "}
                        {selectedLanguage.value === "python" ? (
                            <a
                                href="https://pypi.org/project/inference-sdk/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                inference-sdk
                            </a>
                        ) : (
                            <a
                                href="https://www.npmjs.com/package/@roboflow/inference-sdk"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                @roboflow/inference-sdk
                            </a>
                        )}{" "}
                        package:
                    </BodyText>
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <MarkdownPreview
                            source={
                                selectedLanguage.value === "python"
                                    ? PYTHON_INSTALL_CODE
                                    : JS_INSTALL_CODE
                            }
                        />
                    </div>
                </div>
            </NumberedStep>

            <NumberedStep
                number={getStepNumber()}
                title={
                    selectedLanguage.value === "javascript"
                        ? "Copy and Test in the Browser"
                        : "Copy and Run Your Code"
                }
                collapsible={true}
            >
                <div className="w-full min-w-0 flex-1">
                    <div className="mb-3 flex flex-wrap gap-2">
                        {VIDEO_SOURCE_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => setSelectedSource(option.value)}
                                className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-all ${
                                    selectedSource === option.value
                                        ? "border-brand bg-brand text-white"
                                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                                }`}
                            >
                                <i className={option.icon}></i>
                                {option.text}
                            </button>
                        ))}
                    </div>

                    <div
                        data-color-mode="light"
                        className="w-full overflow-x-auto rounded-lg border border-gray-300"
                    >
                        <MarkdownPreview
                            source={getCodeSnippet()}
                            onCopy={(text) => {
                                trackEvent(WorkflowEvents.DEPLOY_CODE_SNIPPET_COPIED, {
                                    deployMode: isLocal ? "video_local" : "video_api",
                                    language: selectedLanguage.value,
                                    workflowUrl
                                });
                                return replaceApiKeyPlaceholder(text, apiKey);
                            }}
                        />
                    </div>
                </div>
            </NumberedStep>

            {selectedLanguage.value === "javascript" && (
                <NumberedStep
                    number={getStepNumber()}
                    title="For Production Use"
                    collapsible={true}
                >
                    <div className="w-full min-w-0 flex-1 space-y-4">
                        <BodyText>
                            In production, use a backend proxy to keep your API key secure. Replace
                            the connector in your frontend code:
                        </BodyText>

                        <div
                            data-color-mode="light"
                            className="w-full overflow-x-auto rounded-lg border border-gray-300"
                        >
                            <MarkdownPreview source={getProductionConnectorSnippet()} />
                        </div>

                        <BodyText>Then add this proxy endpoint to your backend:</BodyText>

                        <div
                            data-color-mode="light"
                            className="w-full overflow-x-auto rounded-lg border border-gray-300"
                        >
                            <MarkdownPreview source={getProductionBackendSnippet()} />
                        </div>

                        <p className="text-xs text-gray-500">
                            See{" "}
                            <a
                                href="https://github.com/roboflow/inferenceSampleApp"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-brand hover:underline"
                            >
                                inferenceSampleApp
                            </a>{" "}
                            for a complete working example.
                        </p>
                    </div>
                </NumberedStep>
            )}
        </div>
    );
}
